import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { closeHistory, undo } from '@atlaskit/prosemirror-history';

import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pluginKey';
import { endStream } from '../transforms';
import { clampPosition } from '../utils/positionValidation';

import { mapStartPos, mapToPos } from './positionMapping';

/**
 * Discard the streamed content and revert to original.
 * Called when user clicks "Reject" on accept/reject screen.
 */
export function discardStream(view: EditorView, restoreSelection: boolean = true): boolean {
	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(view.state);

	if (!streamingState) {
		return false;
	}

	let { startPos, userContentEndPos } = streamingState;
	const { originalUserContentSlice, streamStartPos } = streamingState;
	const isEmptySelection = startPos === userContentEndPos;

	// Validate we have required state
	if (startPos === undefined || userContentEndPos === undefined || !originalUserContentSlice) {
		view.dispatch(endStream(view.state.tr));
		return false;
	}

	// Step 1: Undo streamed changes
	undo(view.state, view.dispatch);

	// Re-get positions after undo - they may have changed
	const updatedState = purePmInEditorDirectStreamingPluginKey.getState(view.state);
	startPos = updatedState?.startPos ?? startPos;
	userContentEndPos = updatedState?.userContentEndPos ?? userContentEndPos;

	// Step 2: Build cleanup transaction
	let tr = closeHistory(view.state.tr);

	// Step 3: Restore selection if requested
	const decorationState = aiExperienceDecorationPluginKey.getState(view.state);
	if (restoreSelection && !decorationState?.sessionEnded) {
		const docSize = tr.doc.content.size;
		let anchor: number;
		let head: number;

		if (isEmptySelection) {
			anchor = clampPosition(streamStartPos ?? startPos, docSize);
			head = anchor;
		} else {
			anchor = clampPosition(mapStartPos({ pos: startPos, tr }), docSize);
			head = clampPosition(mapToPos({ pos: userContentEndPos, tr }), docSize);
		}

		// Handle reversed selection
		if (
			decorationState?.initialSelection &&
			decorationState.initialSelection.head < decorationState.initialSelection.anchor
		) {
			[anchor, head] = [head, anchor];
		}

		// TextSelection.create can throw with invalid positions
		try {
			tr = tr.setSelection(TextSelection.create(tr.doc, anchor, head));
		} catch {
			// Positions invalid - keep default selection
		}
	}

	// Step 4: End stream and dispatch
	view.dispatch(endStream(tr));
	return true;
}
